import { Link } from "@tanstack/react-router";
import { GLOSSARY } from "@/data/glossary";
import type { StoryTheme } from "@/data/stories";

/**
 * Curated glossary cross-links for a story.
 *
 * Inline auto-linking (see StoryProse) only fires when a story genuinely uses a
 * term, which is rare in narrative prose. This block adds a small, hand-mapped
 * set of related glossary entries per theme so every story has meaningful,
 * relevant internal links — the best-practice alternative to keyword stuffing
 * the body copy.
 */

const BY_THEME: Record<StoryTheme, string[]> = {
  Perspective: ["paradox", "koan", "understatement"],
  Resilience: ["stoic-quote-tradition", "exhortation", "meditations"],
  Wisdom: ["aphorism", "proverb", "maxim"],
  Action: ["dictum", "motto", "epictetus-enchiridion"],
  Time: ["adage", "commonplace-book", "sententia"],
  Identity: ["credo", "apologia", "epithet"],
};

/** Terms every story can usefully point at, since each closes on a quotation. */
const ALWAYS = ["pull-quote", "attribution"];

export function StoryGlossaryLinks({ theme, slug }: { theme: StoryTheme; slug: string }) {
  // Deterministic per-story variation so cross-links are not identical sitewide.
  const offset = slug.length % ALWAYS.length;
  const wanted = [...BY_THEME[theme].slice(0, 2), ALWAYS[offset]];
  const entries = wanted
    .map((s) => GLOSSARY.find((g) => g.slug === s))
    .filter((g): g is (typeof GLOSSARY)[number] => Boolean(g));

  if (entries.length === 0) return null;

  return (
    <section className="mt-14 border-t border-border/60 pt-10">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Words for what this story does</div>
      <h2 className="mt-2 font-display text-2xl">Name the technique</h2>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">
        Every story here ends on a quoted line. These entries explain how lines like it are built, credited, and
        misused.
      </p>
      <dl className="mt-6 grid gap-4 sm:grid-cols-2">

        {entries.map((g) => (
          <div key={g.slug} className="rounded-xl border border-border bg-card/40 p-4 transition hover:border-gold">
            <dt>
              <Link
                to="/glossary/$slug"
                params={{ slug: g.slug }}
                className="font-display text-lg text-foreground hover:text-gold transition"
              >
                {g.term}
              </Link>
            </dt>
            <dd className="mt-1 text-sm text-muted-foreground leading-relaxed">{g.short}</dd>
          </div>
        ))}
      </dl>
      <Link to="/glossary" className="mt-6 inline-block text-xs text-gold hover:underline">
        Browse the full quotation glossary →
      </Link>
    </section>
  );
}
